export type TourPlacement = 'auto' | 'top' | 'bottom' | 'left' | 'right';

import type { DemoRole } from '@/lib/demoRole';

export interface TourStep {
  id: string;
  title: string;
  body: string;
  /** CSS selector of the element to spotlight. If missing/not found, the card is centered. */
  target?: string;
  /** Route (path + optional search) the tour should be on for this step. */
  route?: string;
  /** Resolve a route at runtime (e.g. the newest graded session). */
  resolveRoute?: 'latestSession';
  placement?: TourPlacement;
  /** Extra padding around the spotlight cut-out, px. */
  padding?: number;
  /** Optional click on a selector before measuring (e.g. open a sub-tab). */
  clickBefore?: string;
  /** Limit this step to certain demo roles. Omit = every role sees it. */
  roles?: DemoRole[];
}

const DASH = (tab: string) => `/dashboard?tab=${tab}`;

export const GUIDED_TOUR: TourStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to Sellaris',
    body: 'This quick walkthrough takes you through the product itself — practice modes, a real graded call, analytics and leaderboards. You can leave any time and land right back here.',
    route: DASH('practice'),
  },
  {
    id: 'sidebar',
    title: 'Everything in one workspace',
    body: 'Practice, session history, analytics, leaderboards and your team all live in this rail. We will visit each one.',
    target: '[data-tour="sidebar"]',
    placement: 'right',
  },
  {
    id: 'practice-modes',
    title: 'Six ways to practice',
    body: 'Role-plays, cold calls, objection drills, speed sells, negotiations and interviews — each with an AI buyer that adapts to your company, product and persona.',
    target: '[data-tour="scenarios"]',
    route: DASH('practice'),
    placement: 'bottom',
  },
  {
    id: 'create',
    roles: ['student', 'rep', 'professor', 'manager'],
    title: 'Build a scenario in seconds',
    body: 'Point Sellaris at a company and product and it writes a realistic buyer, discovery path and grading rubric for you.',
    target: '[data-tour="create-quick"]',
    route: DASH('create-quick'),
    placement: 'bottom',
  },
  {
    id: 'history',
    title: 'Every rep, every call',
    body: 'Practice history keeps the recording, transcript and graded rubric for every session so coaching is evidence-based.',
    target: '[data-tour="sessions"]',
    route: DASH('recent-sessions'),
    placement: 'top',
  },
  {
    id: 'session-open',
    title: 'Open a real session',
    body: 'Here is an actual graded call. Notice the score, the rubric breakdown and the timeline of the conversation.',
    resolveRoute: 'latestSession',
    target: '[data-tour="session-detail"]',
    placement: 'auto',
  },
  {
    id: 'session-feedback',
    title: 'AI feedback you can coach with',
    body: 'Sellaris scores each criterion, quotes the moment it happened and gives a specific next rep to run. No generic advice.',
    resolveRoute: 'latestSession',
    target: '[data-tour="session-feedback"]',
    placement: 'auto',
  },
  {
    id: 'progress',
    title: 'Progress over time',
    body: 'Score progression shows whether practice is actually working — per rep, per mode, per rubric criterion.',
    target: '[data-tour="progress"]',
    route: DASH('progress'),
    placement: 'top',
  },
  {
    id: 'leaderboards',
    roles: ['student', 'rep', 'professor', 'manager'],
    title: 'Healthy competition',
    body: 'Rank your cohort or your whole org by Sellaris score, streaks and mode-by-mode performance.',
    target: '[data-tour="leaderboards"]',
    route: DASH('leaderboards'),
    placement: 'top',
  },
  {
    id: 'achievements',
    roles: ['student', 'rep'],
    title: 'Streaks that build habits',
    body: 'Badges and streaks keep reps returning without a manager chasing them.',
    target: '[data-tour="achievements"]',
    route: DASH('achievements'),
    placement: 'top',
  },
  {
    id: 'jobs',
    roles: ['student', 'recruiter'],
    title: 'Careers and interviews',
    body: 'Research a role, practice the interview and match a resume to the job description.',
    target: '[data-tour="jobs"]',
    route: DASH('jobs'),
    placement: 'top',
  },
  {
    id: 'teams',
    roles: ['professor', 'manager'],
    title: 'Classes and teams',
    body: 'Group reps or students, post assignments and watch progress roll up at the cohort level.',
    target: '[data-tour="students"]',
    route: DASH('students'),
    placement: 'top',
  },
  {
    id: 'done',
    title: 'That is the tour',
    body: 'You are back where you started. Book a walkthrough and we will wire Sellaris to your own company, products and rubric.',
  },
];

export function tourForRole(role: DemoRole | null): TourStep[] {
  if (!role) return GUIDED_TOUR;
  return GUIDED_TOUR.filter(step => !step.roles || step.roles.includes(role));
}
